<script lang="js">
import { mapGetters } from 'vuex';
import axios from 'axios';
import { RouterLink } from 'vue-router';
import BioCard from "@/components/BioCard.vue";
import EmailCard from "@/components/EmailCard.vue";
import ScheduleCard from "@/components/ScheduleCard.vue";
import HelperFunctions from "@/utils/HelperFunctions";

export default {
  name: "StaffDashboard",
  data() {
    return {
      logger: null,
      helperFunctions: new HelperFunctions(),
      system_enabled: false,
      loading: false,
      statsLoading: false,
      recentApplicationsLoading: false,
      currentSession: null,
      recentApplications: [],
      dashboardStats: {
        applications: 0,
        enrolledStudents: 0,
        totalRevenue: 0,
        totalUsers: 0,
      },
      bioData: [
        { key: 'staffNumber', label: 'Staff No' },
        { key: 'staffType', label: 'Type' },
        { key: 'accessRoleId.name', label: 'Role' },
        { key: 'userId.email', label: 'Email' },
        { key: 'userId.phone', label: 'Phone' },
        { key: 'status', label: 'Status' }
      ]
    };
  },
  created() {
    // Create a logger instance for this component
    this.logger = this.$createLogger('staffDashboard');
  },
  computed: {
    ...mapGetters(['getUser', 'getStaff']),
    user() {
      return this.getUser;
    },
    staff() {
      this.logger.info("Staff user data: ", JSON.stringify(this.getStaff, null, 2));
      return this.getStaff;
    },
    staffBioData() {
      if (!this.staff) {
        return {};
      }

      // Authentication identity belongs to session.user. The staff record only
      // contains employment and access-control information.
      return { ...this.staff, userId: this.user || {} };
    },
    fullName() {
      if (this.user) {
        return `${this.user.firstName || ""} ${this.user.lastName || ""}`;
      }
      return '';
    },
    staffRole() {
      if (this.staff && this.staff.accessRoleId) {
        return this.staff.accessRoleId.name;
      }
      return 'Staff';
    },
    sessionSummaryText() {
      if (this.currentSession?.sessionYear) {
        return `Here's a summary of the portal's activity for ${this.currentSession.sessionYear} session.`;
      }
      return "Here's a summary of the portal's activity for the current session.";
    },
    formattedRevenue() {
      return `₦${Number(this.dashboardStats.totalRevenue || 0).toLocaleString('en-NG')}`;
    },
  },
  async mounted() {
    // Check if staff is logged in
    if (!this.staff) {
      this.$router.push({ name: 'staffLogin' });
      return;
    }

    await this.refreshDashboard();
  },
  methods: {
    async refreshDashboard() {
      await Promise.all([this.fetchDashboardStats(), this.fetchRecentApplications()]);
    },
    async fetchDashboardStats() {
      try {
        this.statsLoading = true;
        const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/staff/dashboard-stats`);

        if (response.data?.success) {
          this.currentSession = response.data.data.currentSession;
          this.dashboardStats = {
            applications: response.data.data.stats?.applications || 0,
            enrolledStudents: response.data.data.stats?.enrolledStudents || 0,
            totalRevenue: response.data.data.stats?.totalRevenue || 0,
            totalUsers: response.data.data.stats?.totalUsers || 0,
          };
        }
      } catch (error) {
        this.logger.error('Error fetching dashboard stats:', error);
      } finally {
        this.statsLoading = false;
      }
    },
    async fetchRecentApplications() {
      try {
        this.recentApplicationsLoading = true;
        const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/applications`, {
          params: {
            page: 1,
            limit: 5,
            sortBy: 'createdAt',
            sortOrder: 'desc',
          },
        });

        if (response.data?.success) {
          this.recentApplications = response.data.data?.applications || [];
        } else {
          this.recentApplications = [];
        }
      } catch (error) {
        this.logger.error('Error fetching recent applications:', error);
        this.recentApplications = [];
      } finally {
        this.recentApplicationsLoading = false;
      }
    },
    getProgramNameById(programOffering) {
      if (!programOffering || typeof programOffering !== 'object') {
        return 'N/A';
      }

      const programType = programOffering.structure?.type?.type || '';
      const programMode = programOffering.structure?.mode?.description || '';
      const programName = programOffering.program?.name || '';

      if (programType && programMode && programName) {
        return `${programType} ${programMode} ${programName}`;
      }
      return programName || 'N/A';
    },
    getStatusLabel(status) {
      const labels = {
        draft: 'Draft',
        submitted: 'Submitted',
        admission_offered: 'Admission Offered',
        rejected: 'Rejected',
        withdrawn: 'Withdrawn',
        enrolled: 'Enrolled',
      };
      return labels[String(status || '').toLowerCase()] || 'Unknown';
    },
    getStatusBadgeColor(status) {
      switch (String(status || '').toLowerCase()) {
        case 'enrolled':
        case 'admission_offered':
          return 'success';
        case 'draft':
        case 'submitted':
          return 'warning';
        case 'rejected':
          return 'danger';
        case 'withdrawn':
          return 'secondary';
        default:
          return 'secondary';
      }
    },
    formatDate(dateValue) {
      if (!dateValue) {
        return 'N/A';
      }
      try {
        return new Date(dateValue).toLocaleDateString();
      } catch (error) {
        return 'N/A';
      }
    },
  },
  components: { BioCard, EmailCard, ScheduleCard, RouterLink },
};
</script>

<template>
  <div class="col-lg-12 col-md-12">
    <div class="p-4 text-start">
      <div
        class="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3 mb-4"
      >
        <div class="mt-4">
          <h3 class="fw-bold">Welcome, {{ user?.firstName || "Staff" }}!</h3>
          <p class="">
            {{ sessionSummaryText }}
          </p>
        </div>

        <button
          class="btn btn-sm btn-outline-primary"
          @click="refreshDashboard"
          :disabled="statsLoading || recentApplicationsLoading"
        >
          <i
            class="bi bi-arrow-clockwise me-1"
            :class="{ spin: statsLoading || recentApplicationsLoading }"
          ></i>
          Refresh
        </button>
      </div>

      <div class="row g-4 mb-5">
        <div class="col-md-3">
          <div class="card border-0 shadow-sm py-2">
            <div class="card-body d-flex align-items-center gap-3">
              <i
                class="bi bi-person-plus h4 rounded-circle bg-primary-subtle text-primary d-inline-flex align-items-center justify-content-center flex-shrink-0 mb-0"
                style="width: 3rem; height: 3rem"
              ></i>

              <div>
                <h6 class="card-subtitle mb-1 text-body-secondary">
                  Applications
                </h6>
                <h3 v-if="!statsLoading" class="card-title mb-0">
                  {{ dashboardStats.applications }}
                </h3>
                <h3
                  v-else
                  class="card-title mb-0 placeholder-glow"
                  aria-hidden="true"
                >
                  <span class="placeholder col-6"></span>
                </h3>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card border-0 shadow-sm py-2">
            <div class="card-body d-flex align-items-center gap-3">
              <i
                class="bi bi-person-check h4 rounded-circle bg-info-subtle text-info d-inline-flex align-items-center justify-content-center flex-shrink-0 mb-0"
                style="width: 3rem; height: 3rem"
              ></i>

              <div>
                <h6 class="card-subtitle mb-1 text-body-secondary">
                  Enrolled Students
                </h6>
                <h3 v-if="!statsLoading" class="card-title mb-0">
                  {{ dashboardStats.enrolledStudents }}
                </h3>
                <h3
                  v-else
                  class="card-title mb-0 placeholder-glow"
                  aria-hidden="true"
                >
                  <span class="placeholder col-5"></span>
                </h3>
              </div>
            </div>
          </div>
        </div>
        <div
          class="col-md-3"
          v-if="$store.getters.hasPermission('payments', 'manage')"
        >
          <div class="card border-0 shadow-sm py-2">
            <div class="card-body d-flex align-items-center gap-3">
              <i
                class="bi bi-piggy-bank h4 rounded-circle bg-success-subtle text-success d-inline-flex align-items-center justify-content-center flex-shrink-0 mb-0"
                style="width: 3rem; height: 3rem"
              ></i>

              <div>
                <h6 class="card-subtitle mb-1 text-body-secondary">
                  Total Revenue
                </h6>
                <h3 v-if="!statsLoading" class="card-title mb-0">
                  {{ formattedRevenue }}
                </h3>
                <h3
                  v-else
                  class="card-title mb-0 placeholder-glow"
                  aria-hidden="true"
                >
                  <span class="placeholder col-7"></span>
                </h3>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card border-0 shadow-sm py-2">
            <div class="card-body d-flex align-items-center gap-3">
              <i
                class="bi bi-people h4 rounded-circle bg-warning-subtle text-warning d-inline-flex align-items-center justify-content-center flex-shrink-0 mb-0"
                style="width: 3rem; height: 3rem"
              ></i>

              <div>
                <h6 class="card-subtitle mb-1 text-body-secondary">
                  Total Users
                </h6>
                <h3 v-if="!statsLoading" class="card-title mb-0">
                  {{ dashboardStats.totalUsers }}
                </h3>
                <h3
                  v-else
                  class="card-title mb-0 placeholder-glow"
                  aria-hidden="true"
                >
                  <span class="placeholder col-4"></span>
                </h3>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row g-4 mb-5">
        <div class="col-md-6 col-lg-3">
          <h6>Biodata</h6>
          <BioCard
            style="min-height: 400px"
            :data="staffBioData"
            :fields="bioData"
          />
        </div>
        <div class="col-md-6 col-lg-3">
          <h6>E-mail</h6>
          <EmailCard :enabled="false" style="min-height: 400px"></EmailCard>
        </div>
        <div class="col-md-12 col-lg-6">
          <h6>Schedule</h6>
          <ScheduleCard
            :enabled="false"
            style="min-height: 400px"
          ></ScheduleCard>
        </div>
      </div>

      <div class="row g-4">
        <div class="col-md-8">
          <h6>Recent Applications</h6>
          <div class="card border-0 shadow-sm">
            <div class="card-body p-0">
              <table class="table">
                <thead>
                  <tr class="table-dark small">
                    <th scope="col">#</th>
                    <th scope="col">Applicant</th>
                    <th scope="col">Program</th>
                    <th scope="col">Date Applied</th>
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-if="recentApplicationsLoading">
                    <tr v-for="n in 3" :key="`recent-loading-${n}`">
                      <td colspan="5" class="py-2">
                        <div class="placeholder-glow">
                          <span class="placeholder col-12"></span>
                        </div>
                      </td>
                    </tr>
                  </template>
                  <tr v-else-if="!recentApplications.length">
                    <td colspan="5" class="text-center py-3 text-muted">
                      No recent applications available.
                    </td>
                  </tr>
                  <tr
                    v-else
                    v-for="application in recentApplications"
                    :key="application._id"
                  >
                    <td>{{ application.applicationNumber || "N/A" }}</td>
                    <td>
                      {{
                        helperFunctions.toTitleCase(
                          `${application.userId?.firstName || ""} ${
                            application.userId?.otherName || ""
                          } ${application.userId?.lastName || ""}`
                            .replace(/\s+/g, " ")
                            .trim()
                        ) || "N/A"
                      }}
                    </td>
                    <td>
                      {{ getProgramNameById(application.programOfferingId) }}
                    </td>
                    <td>{{ formatDate(application.createdAt) }}</td>
                    <td>
                      <span
                        :class="`badge rounded-pill text-bg-${getStatusBadgeColor(
                          application.status
                        )}`"
                      >
                        {{ getStatusLabel(application.status) }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
              <RouterLink
                class="icon-link icon-link-hover float-end text-decoration-none me-3 mb-4"
                :to="{ name: 'applicationsMgt' }"
              >
                View all applications
                <i class="bi bi-arrow-right d-flex"></i>
              </RouterLink>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <h6>Quick Actions</h6>
          <div class="d-flex flex-column gap-2">
            <RouterLink class="btn btn-dark" :to="{ name: 'applicationsMgt' }">
              <i class="bi bi-plus-circle"></i> Review Applications
            </RouterLink>
            <RouterLink class="btn btn-outline-dark" :to="{ name: 'staffMgt' }">
              <i class="bi bi-person-add"></i> Manage Users
            </RouterLink>
            <RouterLink
              class="btn btn-outline-dark"
              :to="{ name: 'staffReports' }"
            >
              <i class="bi bi-graph-up"></i> Generate Report
            </RouterLink>
            <RouterLink
              class="btn btn-outline-dark"
              :to="{ name: 'staffSettings' }"
            >
              <i class="bi bi-gear"></i> System Settings
            </RouterLink>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.spin {
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
